import React, {useMemo} from "react";
import {DatePicker} from "peggirkit";
import {nl} from "date-fns/locale/nl";
import {startOfDay, startOfYesterday} from "date-fns";
import {useIntl} from "react-intl";
import {formatDate} from "@/utils/DateUtils";
import {useTooltip} from "@widgets/tooltip/TooltipProvider";
import {SetStateFn} from "@/utils/SetStateUtils";
import {DayPickerPreset} from "peggirkit/dist/components/DatePicker/DatePickerElements/DatePickerUtils";

type Props = {
    selectedDay: Date,
    setSelectedDay: (day: Date) => void,
    showDatePicker: boolean,
    setShowDatePicker: SetStateFn<boolean>,
};

const DayReportDayPicker = ({selectedDay, setSelectedDay, showDatePicker, setShowDatePicker}: Props) => {
    const intl = useIntl();
    const {applyTooltip} = useTooltip();
    const dateRangePresets: DayPickerPreset[] = useMemo(() => {
        const ranges: DayPickerPreset[] = [
            {
                name: intl.formatMessage({id: "today"}),
                day: startOfDay(new Date()),
            },
            {
                name: intl.formatMessage({id: "yesterday"}),
                day: startOfYesterday(),
            },
        ];

        return ranges.map((r: DayPickerPreset) => ({
            ...r,
            buttonProps: {
                props: {
                    ...applyTooltip(formatDate(r.day)),
                },
            },
        }));
    }, [intl, applyTooltip]);

    return (
        <DatePicker
            title={intl.formatMessage({id: "pickADay"})}
            cancelButton={{text: intl.formatMessage({id: "cancel"})}}
            continueButton={{text: intl.formatMessage({id: "apply"})}}
            show={showDatePicker}
            setShow={setShowDatePicker}
            calendarLocale={nl}
            horizontalViewBreakpoint={"xl"}
            withTimeConfig={{enabled: false}}
            pickerConfig={{
                type: "single",
                defaultDay: selectedDay,
                onContinue: (newDate: Date | undefined) => {
                    if (!newDate) {
                        return;
                    }

                    setSelectedDay(newDate)
                },
                presets: dateRangePresets,
            }}
        />
    );
};

export default DayReportDayPicker;